import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Store, ShoppingBag, RefreshCcw, PlusCircle } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { useCart } from '@/store/cart.store';
import {
  appendOrderItems,
  createOrder,
  getActiveOrder,
  getHallTables,
  type HallTable,
} from '@/services/client';
import { getSocket } from '@/services/socket';
import { formatRub, formatPhone } from '@/utils/format';

// Режимы оформления:
//  hall     — заказ в зале, выбор свободного столика;
//  takeaway — вынос, запросим телефон/адрес;
//  append   — клиент уже сидит за столом, дозаказываем к тому же счёту.
type OrderMode = 'hall' | 'takeaway' | 'append';

export default function Checkout() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { items, establishmentId, total, clear } = useCart();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);

  // По умолчанию — «В зале»: это основной сценарий для посадки.
  const [mode, setMode] = useState<OrderMode>('hall');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [payment, setPayment] = useState<'online' | 'cash'>('online');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (user?.phone && !phone) setPhone(formatPhone(user.phone));
  }, [user?.phone, phone]);

  // Активный заказ клиента (если уже сидит за столом) — даст возможность
  // «добавить к моему заказу» вместо создания нового.
  const activeQ = useQuery({
    queryKey: ['client/active-order', establishmentId],
    queryFn: () => getActiveOrder(establishmentId!),
    enabled: !!establishmentId && !!token,
    staleTime: 5_000,
  });
  const activeOrder = activeQ.data ?? null;
  const hasHallActive =
    !!activeOrder && (activeOrder.tableArea === 'HALL' || activeOrder.orderType === 'HALL');

  // Как только понимаем, что у клиента уже есть открытый стол — предлагаем
  // «append» как дефолт (один раз, пока пользователь сам не переключит).
  const [autoSwitched, setAutoSwitched] = useState(false);
  useEffect(() => {
    if (autoSwitched) return;
    if (hasHallActive) {
      setMode('append');
      setAutoSwitched(true);
    }
  }, [hasHallActive, autoSwitched]);

  const tablesQ = useQuery({
    queryKey: ['client/hall-tables', establishmentId],
    queryFn: () => getHallTables(establishmentId!),
    enabled: !!establishmentId && mode === 'hall',
    // Поллинг оставляем как fallback — основной канал обновлений ниже через socket.
    refetchInterval: mode === 'hall' ? 15_000 : false,
  });

  // Realtime: слушаем события заказов и инвалидируем столики/активный заказ.
  // Бэкенд шлёт order.* в комнату est:<id>, куда socket.io-middleware нас
  // подписывает автоматически по токену.
  useEffect(() => {
    if (!establishmentId || !token) return;
    const s = getSocket();
    if (!s) return;
    const refresh = () => {
      qc.invalidateQueries({ queryKey: ['client/hall-tables', establishmentId] });
      qc.invalidateQueries({ queryKey: ['client/active-order', establishmentId] });
    };
    const events = ['order.created', 'order.updated', 'order.paid', 'order.completed', 'order.cancelled'];
    events.forEach((e) => s.on(e, refresh));
    return () => {
      events.forEach((e) => s.off(e, refresh));
    };
  }, [establishmentId, token, qc]);

  const tables = tablesQ.data ?? [];
  const freeTables = useMemo(() => tables.filter((t) => t.is_free), [tables]);

  useEffect(() => {
    if (!selectedTableId) return;
    const stillFree = tables.some((t) => t.id === selectedTableId && t.is_free);
    if (!stillFree) setSelectedTableId(null);
  }, [tables, selectedTableId]);

  const mut = useMutation({
    mutationFn: async () => {
      if (!establishmentId) throw new Error('no est');

      const payloadItems = items.map((i) => ({
        dish_id: i.dishId,
        quantity: i.quantity,
        modifiers: i.modifiers,
        comment: i.comment,
      }));

      if (mode === 'append') {
        if (!activeOrder) throw new Error('Активный заказ не найден');
        await appendOrderItems(activeOrder.id, payloadItems);
        return { kind: 'append' as const, orderId: activeOrder.id };
      }

      if (mode === 'hall' && !selectedTableId) {
        throw new Error('Выберите свободный столик');
      }
      const res = await createOrder({
        establishmentId,
        items: payloadItems,
        comment: comment || undefined,
        order_type: mode === 'hall' ? 'hall' : 'takeaway',
        table_id: mode === 'hall' ? selectedTableId ?? undefined : undefined,
        delivery_address: mode === 'takeaway' ? address || undefined : undefined,
        phone: mode === 'takeaway' ? phone || undefined : undefined,
        payment,
        return_url: `${window.location.origin}/orders`,
      });
      return { kind: 'create' as const, res };
    },
    onSuccess: (out) => {
      clear();
      if (out.kind === 'append') {
        toast.success('Позиции добавлены к вашему заказу');
        // После дозаказа возвращаемся на главную — клиент продолжает смотреть
        // меню и при желании сможет снова добавить что-то к тому же столу.
        navigate('/');
        return;
      }
      if (out.res.confirmationUrl) {
        window.location.href = out.res.confirmationUrl;
      } else {
        toast.success('Заказ создан');
        navigate(`/orders/${out.res.order.id}`);
      }
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Не удалось оформить заказ';
      toast.error(msg);
    },
  });

  if (!token) {
    return (
      <div className="p-6 text-center">
        <div className="text-sm text-slate-500 mb-3">Чтобы оформить заказ, войдите.</div>
        <button className="btn-primary" onClick={() => navigate('/login')}>Войти</button>
      </div>
    );
  }

  if (items.length === 0) {
    navigate('/cart', { replace: true });
    return null;
  }

  const canSubmit =
    !mut.isPending &&
    (mode === 'takeaway' ||
      mode === 'append' ||
      (mode === 'hall' && !!selectedTableId));

  const submitLabel = mut.isPending
    ? 'Оформляем…'
    : mode === 'append'
      ? `Добавить к столу №${activeOrder?.tableNumber ?? ''}`.trim()
      : mode === 'hall' && !selectedTableId
        ? 'Выберите столик'
        : 'Подтвердить';

  return (
    <div className="pt-4 pb-28">
      <div className="px-4 mb-4">
        <h1 className="text-2xl font-semibold">Оформление</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Баннер активного заказа: даёт быстро переключиться между append/new. */}
        {hasHallActive && (
          <section
            className={`rounded-2xl p-4 border ${
              mode === 'append'
                ? 'border-brand-500 bg-brand-50/60'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-brand-600 text-white flex items-center justify-center flex-shrink-0">
                <PlusCircle size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">
                  Вы за столом №{activeOrder?.tableNumber}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Заказ №{activeOrder?.number} • {formatRub(activeOrder?.total ?? 0)}
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('append')}
                className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                  mode === 'append'
                    ? 'border-brand-500 bg-brand-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700'
                }`}
              >
                Добавить к моему заказу
              </button>
              <button
                type="button"
                onClick={() => setMode('hall')}
                className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                  mode === 'hall'
                    ? 'border-brand-500 bg-brand-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700'
                }`}
              >
                Новый заказ
              </button>
            </div>
          </section>
        )}

        {/* Тип заказа — «В зале» всегда первым и по умолчанию. */}
        {mode !== 'append' && (
          <section className="rounded-2xl bg-white border border-slate-100 p-3">
            <div className="grid grid-cols-2 gap-2">
              <TypeTile
                active={mode === 'hall'}
                icon={<Store size={18} />}
                title="В зале"
                desc="Выбор столика"
                onClick={() => setMode('hall')}
              />
              <TypeTile
                active={mode === 'takeaway'}
                icon={<ShoppingBag size={18} />}
                title="Вынос"
                desc="Заберу/привезут"
                onClick={() => setMode('takeaway')}
              />
            </div>
          </section>
        )}

        {/* Свободные столики зала — realtime. */}
        {mode === 'hall' && (
          <section className="rounded-2xl bg-white border border-slate-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Свободные столики</h3>
              <button
                type="button"
                onClick={() => tablesQ.refetch()}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Обновить"
              >
                <RefreshCcw size={16} />
              </button>
            </div>
            {tablesQ.isLoading ? (
              <div className="text-sm text-slate-400 text-center py-4">Загрузка…</div>
            ) : freeTables.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-4">
                Сейчас свободных столиков нет. Попробуйте позже или оформите вынос.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {tables.map((t) => (
                  <TableChip
                    key={t.id}
                    table={t}
                    active={selectedTableId === t.id}
                    onClick={() => t.is_free && setSelectedTableId(t.id)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Вынос: телефон + адрес */}
        {mode === 'takeaway' && (
          <>
            <section className="rounded-2xl bg-white border border-slate-100 p-4">
              <h3 className="font-semibold mb-2">Телефон для связи</h3>
              <input
                className="input"
                inputMode="tel"
                placeholder="+7 (___) ___-__-__"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
              />
            </section>

            <section className="rounded-2xl bg-white border border-slate-100 p-4">
              <h3 className="font-semibold mb-2">Адрес доставки</h3>
              <textarea
                className="input"
                rows={2}
                placeholder="Оставьте пустым — заберу сам"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </section>
          </>
        )}

        {/* Комментарий имеет смысл только когда это новый заказ. */}
        {mode !== 'append' && (
          <section className="rounded-2xl bg-white border border-slate-100 p-4">
            <h3 className="font-semibold mb-2">Комментарий</h3>
            <textarea
              className="input"
              rows={2}
              placeholder="Пожелания к заказу"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </section>
        )}

        {/* Способ оплаты: только при создании нового заказа. Добавление позиций
            к существующему заказу оплачивается при расчёте за столиком. */}
        {mode !== 'append' && (
          <section className="rounded-2xl bg-white border border-slate-100 p-4">
            <h3 className="font-semibold mb-3">Способ оплаты</h3>
            <div className="space-y-2">
              <PayOpt active={payment === 'online'} onClick={() => setPayment('online')}
                title="Онлайн (карта/ЮKassa)" desc="Оплата сразу в приложении" />
              <PayOpt active={payment === 'cash'} onClick={() => setPayment('cash')}
                title="При получении / на кассе" desc="Оплатите кассиру" />
            </div>
          </section>
        )}

        {mode === 'append' && (
          <section className="rounded-2xl bg-brand-50/40 border border-brand-200 p-4 text-sm text-slate-600">
            Новые позиции будут добавлены к вашему счёту за столом №{activeOrder?.tableNumber}. Оплата — у кассира при расчёте.
          </section>
        )}
      </div>

      <div className="fixed bottom-16 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-100 safe-bottom">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <div>
            <div className="text-xs text-slate-500">
              {mode === 'append' ? 'К добавлению' : 'К оплате'}
            </div>
            <div className="text-lg font-semibold">{formatRub(total())}</div>
          </div>
          <button
            className="btn-primary flex-1 disabled:opacity-50"
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────

function TypeTile({
  active, icon, title, desc, onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-start gap-1 p-3 rounded-xl border transition-colors text-left ${
        active
          ? 'border-brand-500 bg-brand-50/60'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {icon}
      </div>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </button>
  );
}

function TableChip({
  table, active, onClick,
}: {
  table: HallTable;
  active: boolean;
  onClick: () => void;
}) {
  const disabled = !table.is_free;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`aspect-square rounded-xl border text-center flex flex-col items-center justify-center transition-colors ${
        active
          ? 'border-brand-500 bg-brand-600 text-white'
          : disabled
            ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
            : 'border-slate-200 bg-white text-slate-700 hover:border-brand-400'
      }`}
    >
      <div className="text-base font-semibold tabular-nums leading-none">
        №{table.number}
      </div>
      <div className={`text-[10px] mt-1 ${active ? 'text-white/90' : 'text-slate-400'}`}>
        {disabled ? 'занят' : `${table.seats} мест`}
      </div>
    </button>
  );
}

function PayOpt({ active, onClick, title, desc }: {
  active: boolean; onClick: () => void; title: string; desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-3 rounded-xl border transition-colors ${
        active ? 'border-brand-500 bg-brand-50/60' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="font-medium">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </button>
  );
}
